import React, { useState } from 'react';
import { Button, InputText, Link } from '../ui';
import SimpleDialog from '../dialogs/SimpleDialog/SimpleDialog';
import WarningIcon from '../WarningIcon/WarningIcon';
import { PaySubmissionDTO, Submission } from '../../store/submission/types';
import { useTranslation } from 'react-i18next';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import submissionSlice from '../../store/submission/slice';
import { connect } from 'react-redux';

interface Props {
  submission: Submission;
  personId: number;
  paySubmissionAction: (data: PaySubmissionDTO) => void;
}

const Payment: React.FC<Props> = ({ submission, personId, paySubmissionAction }) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);

  const payerHasChanged = submission.payment?.person_id != null && personId != submission.payment.person_id;
  const [confirmation, setConfirmation] = useState<string>('');
  const validationString = 'I AGREE';

  const redirectToPayment = (submissionId: number, changePayer?: boolean) =>
    paySubmissionAction({ submission_id: submissionId, change_payer: changePayer, from_url: window.location.href });

  if (!submission.payment) return <></>;

  return (
    <div className="mt-3 submissionPaymentWrapper">
      <div className="submissionPaymentText">
        <strong>{t('Payment') + ': '}</strong>
        <span className={`font-semibold ${submission.payment.paid ? 'text-green-600' : 'text-red-600'}`}>
          {submission.payment.paid ? t('Paid') : t('Pending')}
        </span>
        {submission.payment.paid && personId == submission.payment.person_id && (
          <Link className="ml-3 cursor-pointer" onClick={() => redirectToPayment(submission.id)}>
            {t('View details')}
          </Link>
        )}
      </div>

      {!submission.payment.paid && (
        <Button
          roundedEdges="rounded"
          className="submissionPaymentButton"
          onClick={() => {
            if (payerHasChanged) {
              // Payment has been started by another author
              setDialog(
                <SimpleDialog
                  open={true}
                  handleClose={() => setDialog(undefined)}
                  handleOk={() => setIsOpen(true)}
                  title={
                    <div className="flex items-center gap-x-1">
                      <WarningIcon />
                      <div>
                        <span>{t('Payment already started')}</span>
                      </div>
                    </div>
                  }
                >
                  <p className="text-sm">{t('Another author has already started the payment process.')}</p>
                  <p className="text-sm">{t('If you proceed, the current payment will be canceled.')}</p>
                </SimpleDialog>,
              );
            } else if (submission.payment?.person_id == null) {
              // Payment has not been started
              setIsOpen(true);
            } else {
              // Payment started by current user
              redirectToPayment(submission.id);
            }
          }}
        >
          {t('Pay')}
        </Button>
      )}
      {dialog}
      {isOpen && (
        <SimpleDialog
          handleClose={() => {
            setConfirmation('');
            setIsOpen(false);
          }}
          handleOk={() => redirectToPayment(submission.id, payerHasChanged)}
          disableOkButton={confirmation != validationString}
          title={t('Pay submission')}
          width="48rem"
        >
          <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
            <p className="font-medium text-blue-900">Payment information</p>
            <p className="mt-1 text-blue-800">
              Payments are processed securely through the <span className="font-semibold">Stripe</span> platform. You
              will be <span className="font-semibold">redirected to Stripe</span> to complete the payment and then be
              able to return to chairingtool once the process is finished.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900">STEP 1</h3>
              <p className="mt-1">
                You must click on "Update information" under "BILLING INFORMATION" if you want your institutional
                address, full name, tax ID etc to appear on the invoice. Please{' '}
                <span className="font-semibold">
                  do not click on the payment button unless you want anonymous payee information to be displayed on
                  yourinvoice
                </span>
                .
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">STEP 2</h3>
              <p className="mt-1">Add payment method. Default payment method is credit card.</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">STEP 3</h3>
              <p className="mt-1">Click the "PAY INVOICE" button.</p>
              <p className="mt-2 text-red-600 font-medium">
                ⚠️ The invoice can NOT be changed after payment was provided.
              </p>
            </div>
          </div>
          <p className="mt-5 mb-1">
            {t('In order to proceed, type')} <b>{validationString}</b>:
          </p>
          <InputText onChange={(e) => setConfirmation(e.target.value)} />
        </SimpleDialog>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  paySubmissionAction: (data: PaySubmissionDTO) => dispatch(submissionSlice.actions.PAY(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Payment);
